import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { onSessionExpired, clearSessionToken, storeSessionToken, sessionSnapshot, isCurrentSession, InvalidSessionError } from "@/lib/session-expiration";
import { clearAuthenticatedCache } from "@/lib/query-client";
import {
  clearSentryUserContext,
  setSentryUserContext,
} from "@/sentry";

import { getCurrentUser, loginUser } from "../api/authApi";
import type { AuthUser, LoginRequest } from "../types/auth";

type AuthenticationResult = {
  access_token: string;
  token_type: string;
  user: AuthUser;
};

type AuthContextValue = {
  user: AuthUser | null;
  churchId: string | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (data: LoginRequest) => Promise<void>;

  completeInvitationActivation: (
    result: AuthenticationResult,
  ) => void;

  logout: () => void;
};

// Context is exported alongside its provider to preserve the existing consumer API.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<
  AuthContextValue | undefined
>(undefined);

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<AuthUser | null>(null);

  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("access_token"),
  );

  const [isLoading, setIsLoading] = useState(true);

  const clearAuthentication = useCallback(() => {
    clearSessionToken("church");
    clearAuthenticatedCache();
    clearSentryUserContext();
    setToken(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  useEffect(() => onSessionExpired((scope) => {
    if (scope === "church") {
      setToken(null);
      setUser(null);
      setIsLoading(false);
    }
  }), []);

  useEffect(() => {
    let active = true;
    const snapshot = sessionSnapshot("church");
    async function loadUser() {
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await getCurrentUser();
        if (active && isCurrentSession("church", snapshot)) {
          setUser(currentUser);
        }
      } catch {
        // Invalid sessions are handled centrally. Other failures preserve credentials.
      } finally {
        if (active) setIsLoading(false);
      }
    }

    void loadUser();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!user) {
      clearSentryUserContext();
      return;
    }

    setSentryUserContext({
      userId: user.id,
      churchId: user.church_id,
      role: user.role,
    });
  }, [user]);

  function storeAuthentication(
    result: AuthenticationResult,
  ) {
    clearAuthenticatedCache();
    storeSessionToken("church", result.access_token);

    setToken(result.access_token);
    setUser(result.user);
  }

  async function login(data: LoginRequest) {
    const snapshot = sessionSnapshot("church");
    const response = await loginUser(data);
    if (!isCurrentSession("church", snapshot)) throw new InvalidSessionError();

    storeAuthentication(response);
  }

  function completeInvitationActivation(
    result: AuthenticationResult,
  ) {
    storeAuthentication(result);
  }

  function logout() {
    clearAuthentication();
  }

  const value = {
    user,
    churchId: user?.church_id ?? null,
    token,
    isAuthenticated: Boolean(user && token),
    isLoading,
    login,
    completeInvitationActivation,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {token && !user && !isLoading ? (
        <div role="alert" className="p-6">
          <p>Unable to access your account. Please try again or contact your administrator.</p>
          <button onClick={() => window.location.reload()}>Try again</button>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}
